import Model from '$dependencies/Model';
import PhotoCollection from '$collections/PhotoCollection';
import TaxonomyModel from '$models/TaxonomyModel';
import Config from '$root/Config';

const FIELDS = {
	uuid: { type: String, default: '', identifier: true },
	customId: { type: String, default: '' },
	title: { type: String, default: '' },
	street: { type: String, default: '' },
	city: { type: String, default: '' },
	description: { type: String, default: '' },
	reporterEmail: { type: String, default: '' },
	reporterName: { type: String, default: '' },
	reporterPhone: { type: String, default: '' },
	reporterSubscribed: { type: Boolean, default: false },
	volume: { type: Number, default: 0 },
	location: { type: Object, default: () => ({ lat: null, lon: null }) },
	category: { type: TaxonomyModel, default: () => new TaxonomyModel() },
	postalCode: { type: Object, default: () => ({}) },
	community: { type: Object, default: () => ({}) },
	images: { type: Object, default: () => new PhotoCollection() },
};

let id = 0;

export default class IncidentCandidateModel extends Model {
	constructor(args = {}) {
		args = Object.assign({}, args);
		args.fields = FIELDS;
		args.identifier = args.identifier || 'uuid';
		super(args);

		this.instance_id = id++;
	}

	post(data) {
		this.setConfig('url', Config.getUrl('c_incidentCandidate'));
		// API returns the custom id as plain text (e.g. "BXL-B-887097").
		this.setConfig('responseType', 'text');
		this.setConfig('headers', {
			Accept: 'application/json, text/plain, */*',
			'Content-Type': 'application/json',
		});
		return super.post(prepareDataForPost(data || this.toObject(true)));
	}

	parse(data) {
		if (data == null || data === '') return {};

		if (typeof data === 'string') {
			const customId = data.trim().replace(/^"|"$/g, '');
			return super.parse({ customId });
		}

		const payload = data.data ? { ...data.data } : { ...data };
		if (typeof payload === 'string') {
			const customId = payload.trim().replace(/^"|"$/g, '');
			return super.parse({ customId });
		}
		if (payload.id && !payload.uuid) {
			payload.uuid = payload.id;
		}
		if (payload.custom_id && !payload.customId) {
			payload.customId = payload.custom_id;
		}

		return super.parse(payload);
	}

	clearAddress() {
		this.street = this.city = '';
		this.postalCode = {};
		this.location = { lat: null, lon: null };
	}

	destroy() {}

	get id() {
		return this.uuid;
	}

	set id(val) {
		this.uuid = val;
	}

	get custom_id() {
		return this.customId;
	}

	set custom_id(val) {
		this.customId = val;
	}

	get reporter_email() {
		return this.reporterEmail;
	}

	set reporter_email(val) {
		this.reporterEmail = val;
	}

	get reporter_name() {
		return this.reporterName;
	}

	set reporter_name(val) {
		this.reporterName = val;
	}

	get reporter_phone() {
		return this.reporterPhone;
	}

	set reporter_phone(val) {
		this.reporterPhone = val;
	}

	get reporter_subscribed() {
		return this.reporterSubscribed;
	}

	set reporter_subscribed(val) {
		this.reporterSubscribed = val;
	}

	get post_code() {
		if (!this.postalCode) return '';
		if (typeof this.postalCode === 'string' || typeof this.postalCode === 'number') {
			return String(this.postalCode);
		}
		return String(this.postalCode.code || this.postalCode.postalCode || this.postalCode.title || '');
	}

	set post_code(val) {
		if (val && typeof val === 'object') {
			this.postalCode = val;
			return;
		}

		const code = val == null ? '' : String(val);
		this.postalCode = {
			...(this.postalCode || {}),
			code,
			postalCode: code,
			title: code,
		};
	}

	get post_code_ref() {
		return this.postalCode;
	}

	set post_code_ref(val) {
		this.postalCode = val || {};
	}

	get coordinates() {
		return { latitude: this.location.lat, longitude: this.location.lon };
	}

	get gmapCoordinates() {
		return { lat: this.location.lat, lng: this.location.lon };
	}

	get latitude() {
		return this.location.lat;
	}

	get longitude() {
		return this.location.lon;
	}

	set latitude(val) {
		this.location.lat = val;
	}

	set longitude(val) {
		this.location.lon = val;
	}

	get fullAddress() {
		let full = '';
		const postal = this.post_code;

		if (this.street) full += this.street;
		if (this.street && (postal || this.city)) full += ', ';
		if (postal) full += postal;
		if (this.city) full += ` ${this.city}`;

		return full;
	}
}

function entityId(value) {
	if (!value) return '';
	if (typeof value === 'string' || typeof value === 'number') return String(value);
	return value.uuid || value.id || '';
}

function prepareDataForPost(data) {
	const location = data.location || {};
	const payload = {
		community: entityId(data.community),
		street: data.street || '',
		city: data.city || '',
		postalCode: entityId(data.postalCode),
		location: {
			lat: location.lat,
			lon: location.lon,
		},
		category: entityId(data.category),
		reporterName: data.reporterName || '',
		reporterEmail: data.reporterEmail || '',
		reporterSubscribed: !!data.reporterSubscribed,
	};

	if (data.description) {
		payload.description = data.description;
	}

	if (data.reporterPhone) {
		payload.reporterPhone = data.reporterPhone;
	}

	return payload;
}
